import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { pushChat } from '../utils/chatStore';

// Manages chat state, message subscription, sending, auto-scroll, and auto-focus.
// Chats live in /games/{gameId}/chats — hidden from spectators by Firestore rules.
export const useChat = (gameId, game, currentUser, isAdmin, playerData, playerTeam) => {
  const [messageInput, setMessageInput] = useState('');
  // Admin gets independent input boxes for white + black chats
  const [whiteAdminInput, setWhiteAdminInput] = useState('');
  const [blackAdminInput, setBlackAdminInput] = useState('');
  const [generalMessageInput, setGeneralMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatTab, setChatTab] = useState('chat');
  const [adminChatTeam, setAdminChatTeam] = useState('white');
  const [rawChats, setRawChats] = useState([]);

  const whiteChatRef = useRef(null);
  const blackChatRef = useRef(null);
  const generalChatRef = useRef(null);
  const chatInputRef = useRef(null);

  // Subscribe to the chats subcollection
  useEffect(() => {
    if (!gameId) return;
    const q = query(collection(db, 'games', gameId, 'chats'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      setRawChats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Chat subscription error:', err);
    });
    return () => unsubscribe();
  }, [gameId]);

  // Split into white/black/general buckets (a chat with team='white' shows in white, etc.)
  const messages = useMemo(() => {
    const white = [];
    const black = [];
    const general = [];
    for (const m of rawChats) {
      if (m.team === 'white') white.push(m);
      else if (m.team === 'black') black.push(m);
      else if (m.team === 'general') general.push(m);
    }
    return { white, black, general };
  }, [rawChats]);

  // Auto-scroll team chats
  useEffect(() => {
    if (whiteChatRef.current) {
      whiteChatRef.current.scrollTop = whiteChatRef.current.scrollHeight;
    }
    if (blackChatRef.current) {
      blackChatRef.current.scrollTop = blackChatRef.current.scrollHeight;
    }
  }, [messages.white, messages.black]);

  // Auto-scroll general chat
  useEffect(() => {
    if (generalChatRef.current) {
      generalChatRef.current.scrollTop = generalChatRef.current.scrollHeight;
    }
  }, [messages.general]);

  // Keep chat input focused for players
  useEffect(() => {
    if (isAdmin || !playerTeam) return;

    const focusChatInput = () => {
      if (chatInputRef.current && document.activeElement !== chatInputRef.current) {
        chatInputRef.current.focus();
      }
    };

    focusChatInput();

    const handleClick = (e) => {
      const tagName = e.target.tagName.toLowerCase();
      const isInteractive = tagName === 'button' || tagName === 'select' || tagName === 'a';
      if (!isInteractive) {
        setTimeout(focusChatInput, 10);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Enter') {
        setTimeout(focusChatInput, 10);
      }
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isAdmin, playerTeam, chatTab]);

  const handleSendMessage = async (team) => {
    // For admin, pick the input that matches the team being sent to.
    // For players, the single shared input is used.
    const text = isAdmin
      ? (team === 'white' ? whiteAdminInput : blackAdminInput)
      : messageInput;
    if (!text.trim() || sending || !game) return;

    try {
      setSending(true);

      const messageData = {
        sender: isAdmin ? 'ADMIN' : (playerData?.nickname || currentUser.email),
        message: text.trim(),
        role: isAdmin ? 'Admin' : (playerData?.role || null),
        timestamp: new Date().toISOString(),
        email: currentUser.email,
        isAdmin: isAdmin || false,
        team,
      };

      await pushChat(gameId, messageData);
      if (isAdmin) {
        if (team === 'white') setWhiteAdminInput('');
        else setBlackAdminInput('');
      } else {
        setMessageInput('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSendGeneralMessage = async () => {
    if (!generalMessageInput.trim() || sending || !game) return;

    try {
      setSending(true);

      const senderName = isAdmin ? 'ADMIN' : (playerData?.nickname || currentUser.email);
      const messageData = {
        sender: senderName,
        message: generalMessageInput.trim(),
        senderTeam: isAdmin ? 'admin' : (playerTeam || null),
        timestamp: new Date().toISOString(),
        email: currentUser.email,
        isAdmin: isAdmin || false,
        team: 'general',
      };

      await pushChat(gameId, messageData);
      setGeneralMessageInput('');
    } catch (error) {
      console.error('Error sending general message:', error);
    } finally {
      setSending(false);
    }
  };

  return {
    messageInput,
    setMessageInput,
    whiteAdminInput,
    setWhiteAdminInput,
    blackAdminInput,
    setBlackAdminInput,
    generalMessageInput,
    setGeneralMessageInput,
    sending,
    chatTab,
    setChatTab,
    adminChatTeam,
    setAdminChatTeam,
    whiteChatRef,
    blackChatRef,
    generalChatRef,
    chatInputRef,
    handleSendMessage,
    handleSendGeneralMessage,
    messages,
  };
};
